import React from 'react';
import {
  SiApple,
  SiSamsung,
  SiAsus,
  SiDell,
  SiHp,
  SiLenovo,
  SiOneplus,
  SiXiaomi,
  SiOppo,
  SiAcer,
  SiMotorola,
  SiSony,
  SiV,
  SiHuawei
} from 'react-icons/si';
import { Building, Shield } from 'lucide-react';
import './Benefits.css';

// Specified 8 logo-only brands, with extra boost (iconSize: 48) for Samsung, Lenovo, Acer, and Sony
const BRANDS_DATA = [
  { icon: SiOneplus, name: "OnePlus" },
  { icon: SiXiaomi, name: "Xiaomi" },
  { icon: SiOppo, name: "Oppo", hideName: true },
  { icon: Building, name: "Microsoft" },
  { icon: SiAcer, name: "Acer", hideName: true, iconSize: 48 },
  { icon: Shield, name: "MSI" },
  { icon: SiMotorola, name: "Motorola" },
  { icon: SiApple, name: "Apple" },
  { icon: SiSamsung, name: "Samsung", hideName: true, iconSize: 48 },
  { icon: SiDell, name: "Dell", hideName: true },
  { icon: SiHp, name: "HP", hideName: true },
  { icon: SiLenovo, name: "Lenovo", hideName: true, iconSize: 48 },
  { icon: SiAsus, name: "ASUS", hideName: true },
  { icon: SiV, name: "Vivo" },
  { icon: SiSony, name: "Sony", hideName: true, iconSize: 48 },
  { icon: SiHuawei, name: "Huawei" },
];

const StarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="#C98E32" stroke="none">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C98E32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const HomeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C98E32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const TagIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C98E32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C98E32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C98E32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C98E32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const CrossCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const COMPARISON_ROWS = [
  {
    icon: ClockIcon,
    feature: 'Repair Time',
    local: '2–7 Days',
    localSuccess: false,
    auth: '5–14 Business Days',
    authSuccess: false,
    doorstep: '30 Min to 1 Day',
    doorstepHighlight: true,
  },
  {
    icon: HomeIcon,
    feature: 'Convenience',
    local: 'You Must Visit Twice',
    localSuccess: false,
    auth: 'You Must Visit & Wait',
    authSuccess: false,
    doorstep: 'We Come to Your Doorstep',
    doorstepHighlight: true,
  },
  {
    icon: TagIcon,
    feature: 'Cost',
    local: 'Moderate / Hidden Fees',
    localSuccess: false,
    auth: 'Extremely Expensive',
    authSuccess: false,
    doorstep: 'Affordable & Upfront',
    doorstepHighlight: true,
  },
  {
    icon: SparkleIcon,
    feature: 'Part Quality',
    local: 'Duplicate / Non-OEM',
    localSuccess: false,
    auth: 'Original Apple Part',
    authSuccess: true,
    doorstep: '100% Genuine OEM Grade',
    doorstepHighlight: true,
  },
  {
    icon: AlertCircleIcon,
    feature: 'iOS Part Message',
    local: 'Shows "Unknown Part" Error',
    localSuccess: false,
    auth: 'Original Part (No Error)',
    authSuccess: true,
    doorstep: 'Calibrated OEM (No Error)',
    doorstepHighlight: true,
  },
  {
    icon: ShieldIcon,
    feature: 'Warranty',
    local: '30 Days or None',
    localSuccess: false,
    auth: 'Up to 90 Days',
    authSuccess: true,
    doorstep: 'Up to 1 Year Warranty',
    doorstepHighlight: true,
  },
];

function Benefits() {
  return (
    <section className="cmp-section" id="benefits">
      {/* Background Ambient Ornaments */}
      <div className="cmp-bg-dots cmp-bg-dots-left" aria-hidden="true" />
      <div className="cmp-bg-dots cmp-bg-dots-right" aria-hidden="true" />
      <div className="cmp-bg-glow" aria-hidden="true" />

      <div className="cmp-container">
        {/* Header */}
        <header className="cmp-header">
          <div className="cmp-badge">
            <StarIcon />
            <span>BENEFITS</span>
          </div>

          <h2 className="cmp-title">
            <span className="cmp-title-main">Service</span>
            <span className="cmp-title-gold"> Comparison</span>
          </h2>

          {/* Gold Diamond Divider Ornament */}
          <div className="cmp-heading-divider" aria-hidden="true">
            <div className="cmp-divider-line" />
            <div className="cmp-divider-diamond" />
            <div className="cmp-divider-line" />
          </div>

          <p className="cmp-subtitle">
            See how our doorstep service compares against traditional repair options.
          </p>
        </header>

        {/* 3D Comparison Card Container */}
        <div className="cmp-3d-card">
          <div className="cmp-table-responsive">
            <table className="cmp-table">
              <thead>
                <tr>
                  <th className="cmp-col-feature">Feature</th>
                  <th className="cmp-col-local">Local Repair Shop</th>
                  <th className="cmp-col-auth">Authorized Service Centre</th>
                  <th className="cmp-col-doorstep cmp-highlighted-col">
                    <span className="cmp-doorstep-badge">⭐ BEST CHOICE</span>
                    <span className="cmp-doorstep-heading-text">DSMR Solutions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, index) => {
                  const FeatureIcon = row.icon;
                  return (
                    <tr key={index}>
                      <td className="cmp-row-feature">
                        <div className="cmp-feature-cell">
                          <div className="cmp-feature-icon-box">
                            <FeatureIcon />
                          </div>
                          <span className="cmp-feature-text">{row.feature}</span>
                        </div>
                      </td>

                      <td className={row.localSuccess ? 'cmp-status-success' : 'cmp-status-danger'}>
                        <div className="cmp-cell-value">
                          {row.localSuccess ? <CheckCircleIcon /> : <CrossCircleIcon />}
                          <span>{row.local}</span>
                        </div>
                      </td>

                      <td className={row.authSuccess ? 'cmp-status-success' : 'cmp-status-danger'}>
                        <div className="cmp-cell-value">
                          {row.authSuccess ? <CheckCircleIcon /> : <CrossCircleIcon />}
                          <span>{row.auth}</span>
                        </div>
                      </td>

                      <td className="cmp-status-doorstep">
                        <div className="cmp-cell-value cmp-cell-doorstep-val">
                          <CheckCircleIcon />
                          <span>{row.doorstep}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Integrated Supported Brands Marquee */}
        <div className="cmp-brands-section" id="brands">
          <div className="cmp-brands-header">
            <h3 className="cmp-brands-title">Supported Brands</h3>
            <p className="cmp-brands-subtitle">
              We repair all major brands and models
            </p>
          </div>

          <div className="cmp-scroll-wrapper">
            <div className="cmp-fade cmp-fade-left" aria-hidden="true" />
            <div className="cmp-fade cmp-fade-right" aria-hidden="true" />

            <div className="cmp-scroll-track">
              {[...BRANDS_DATA, ...BRANDS_DATA, ...BRANDS_DATA].map((brand, idx) => {
                const IconComponent = brand.icon;
                const calculatedSize = brand.iconSize || (brand.hideName ? 39 : 20);

                return (
                  <div
                    className={`cmp-pill-tile ${brand.hideName ? 'cmp-icon-only-pill' : ''} ${brand.iconSize ? 'cmp-large-logo-pill' : ''}`}
                    key={idx}
                  >
                    <IconComponent
                      className="cmp-brand-icon"
                      size={calculatedSize}
                    />
                    {!brand.hideName && (
                      <span className="cmp-brand-name">{brand.name}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

export default Benefits;
